import { lazy } from 'react'
import { isRouteErrorResponse, useRouteError, Link, type RouteObject } from 'react-router-dom'
import { TriangleAlert } from 'lucide-react'

import AppShell, { type RouteHandle } from '@/components/AppShell'
import { Button } from '@/components/ui/button'
import { ApiError } from '@/lib/api'

/**
 * 라우트 정의.
 *
 * 🔴 페이지 파일(`src/pages/*.tsx`)은 **다른 담당자가 만듭니다.**
 *    아래 lazy import 는 그 파일들이 놓일 자리를 가리킵니다. 파일이 아직
 *    없으면 `npm run typecheck` 가 그 7개를 TS2307 로 알려 줍니다 —
 *    이 오류는 "아직 안 만들어졌다"는 정확한 신호이므로 stub 으로 덮지
 *    않았습니다. 각 페이지가 무엇을 구현해야 하는지는 `web/PAGES.md` 에
 *    적혀 있습니다.
 *
 *    각 페이지는 **default export 한 개**(props 없는 컴포넌트)를 내보내야 합니다.
 */

// TODO(pages): src/pages/Dashboard.tsx  — PAGES.md §대시보드
const Dashboard = lazy(() => import('./pages/Dashboard'))
// TODO(pages): src/pages/Upload.tsx     — PAGES.md §업로드
const UploadPage = lazy(() => import('./pages/Upload'))
// TODO(pages): src/pages/Jobs.tsx       — PAGES.md §작업 목록
const Jobs = lazy(() => import('./pages/Jobs'))
// TODO(pages): src/pages/JobDetail.tsx  — PAGES.md §작업 상세 · 전사 뷰어
const JobDetail = lazy(() => import('./pages/JobDetail'))
// TODO(pages): src/pages/Library.tsx    — PAGES.md §라이브러리
const LibraryPage = lazy(() => import('./pages/Library'))
// TODO(pages): src/pages/Providers.tsx  — PAGES.md §프로바이더
const Providers = lazy(() => import('./pages/Providers'))
// TODO(pages): src/pages/Costs.tsx      — PAGES.md §비용
const Costs = lazy(() => import('./pages/Costs'))
/**
 * 🔴 전사문 기반 대화는 **독립 라우트가 아닙니다** (회장님 2026-08-11 지시).
 *
 *    "채팅페이지를 따로 둘 필요 없이 분석페이지에 섹션으로 배치하는 게 합리적인
 *     UX 아니겠냐? 어차피 플레이어랑, 전사원문도 같은 페이지에 있어야 좋고."
 *
 *    그래서 `/chat` 을 없애고 작업 상세 안의 섹션
 *    (`components/chat/ChatSection.tsx`)으로 옮겼습니다. 깊은 링크가 필요하면
 *    `/jobs/{id}#chat`(특정 대화는 `?chat={roomId}`)을 쓰십시오.
 *
 *    옛 주소로 들어오시면 빈 화면 대신 그 사실을 알려 드립니다 — 조용히 404 로
 *    떨어뜨리면 "화면이 사라졌다" 로만 보입니다.
 */
const ChatMoved = lazy(() => import('./pages/ChatMoved'))
const Fleet = lazy(() => import('./pages/Fleet'))

/** 🔴 실패는 크게 보입니다. 흰 화면으로 끝내지 않습니다. */
function RouteErrorView() {
  const error = useRouteError()

  let title = '화면을 여는 중 문제가 발생했습니다'
  let detail = ''

  if (isRouteErrorResponse(error)) {
    title = `${error.status} — 요청한 화면을 찾지 못했습니다`
    detail = typeof error.data === 'string' ? error.data : ''
  } else if (error instanceof ApiError) {
    title = error.message
    detail = `오류 코드 ${error.code} (HTTP ${error.status})`
  } else if (error instanceof Error) {
    title = error.message
    detail = error.stack ?? ''
  }

  return (
    <div data-component="route-error" className="flex h-full flex-col items-start gap-3 p-6">
      <div className="flex flex-nowrap items-center gap-2 text-destructive">
        <TriangleAlert className="size-5 shrink-0" aria-hidden />
        <h2 data-role="route-error-title" className="text-base font-semibold">
          {title}
        </h2>
      </div>
      {detail ? (
        <pre
          data-role="route-error-detail"
          className="max-w-full overflow-x-auto rounded-md border border-destructive/40 bg-destructive/10 p-3 text-xs text-destructive"
        >
          {detail}
        </pre>
      ) : null}
      <Button asChild variant="outline" size="sm" data-action="dashboard-from-error">
        <Link to="/">대시보드로 돌아가기</Link>
      </Button>
    </div>
  )
}

function NotFound() {
  return (
    <div data-component="route-not-found" className="flex h-full flex-col items-start gap-3">
      <h2 className="text-base font-semibold">주소에 해당하는 화면이 없습니다</h2>
      <p className="text-sm text-muted-foreground">
        좌측 메뉴에서 원하시는 화면을 골라 주시길 권합니다.
      </p>
      <Button asChild variant="outline" size="sm" data-action="dashboard-from-not-found">
        <Link to="/">대시보드로 돌아가기</Link>
      </Button>
    </div>
  )
}

const handle = (title: string, description?: string): RouteHandle => ({ title, description })

export const routes: RouteObject[] = [
  {
    path: '/',
    element: <AppShell />,
    errorElement: <RouteErrorView />,
    children: [
      {
        index: true,
        element: <Dashboard />,
        handle: handle('대시보드', '최근 작업과 오늘 사용액을 한눈에 보실 수 있습니다.'),
      },
      {
        path: 'upload',
        element: <UploadPage />,
        handle: handle('업로드', '음원을 등록하고 비용 계획을 미리 확인하십시오.'),
      },
      {
        path: 'jobs',
        element: <Jobs />,
        handle: handle('작업', '진행 중인 전사 작업을 실시간으로 보실 수 있습니다.'),
      },
      {
        path: 'jobs/:jobId',
        element: <JobDetail />,
        handle: handle('작업 상세', '파형·화자 트랙·단어 타임스탬프를 함께 봅니다.'),
      },
      {
        path: 'library',
        element: <LibraryPage />,
        handle: handle('라이브러리', '화자·시각·키워드로 전사 자산을 찾으십시오.'),
      },
      {
        // 🔴 옛 주소. 화면은 작업 상세 안으로 옮겨졌습니다.
        path: 'chat',
        element: <ChatMoved />,
        handle: handle(
          '대화 — 작업 상세로 옮겼습니다',
          '전사문에 묻는 자리는 이제 작업 상세 페이지 안에 있습니다. 플레이어·전사 원문과 같은 화면입니다.',
        ),
      },
      {
        path: 'providers',
        element: <Providers />,
        handle: handle('프로바이더', '전사 서비스의 키와 요율을 관리하십시오.'),
      },
      {
        path: 'costs',
        element: <Costs />,
        handle: handle('비용', '계획 대비 실제 지출을 원장으로 확인하십시오.'),
      },
      {
        path: 'fleet',
        element: <Fleet />,
        handle: handle(
          '분산 컴퓨팅',
          '어느 기기에서 무슨 작업이 얼만큼 진행 중인지 확인하십시오.',
        ),
      },
      { path: '*', element: <NotFound />, handle: handle('찾을 수 없는 화면') },
    ],
  },
]
